import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { Get } from "../config/api/get";
import { Post } from "../config/api/post";
import { useSelector } from "react-redux";
import { HOMEWORK } from "../config/constants";

const HOMEWORK_KEY = ["homework"];

const invalidateHomework = (queryClient, id) => {
  queryClient.invalidateQueries({ queryKey: HOMEWORK_KEY });
  if (id) queryClient.invalidateQueries({ queryKey: ["homework-item", id] });
};

export const useGetAllHomework = (params = {}) => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: [...HOMEWORK_KEY, params],
    queryFn: async ({ signal }) => {
      const response = await Get(HOMEWORK.getAllHomework, token, params, { signal });
      return response?.data || { docs: [], totalPages: 1, totalDocs: 0 };
    },
    enabled: !!token,
    placeholderData: keepPreviousData,
  });
};

export const useGetHomeworkById = (id) => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: ["homework-item", id],
    queryFn: async () => {
      const response = await Get(HOMEWORK.getHomeworkById + id, token);
      return response?.data?.homework;
    },
    enabled: !!token && !!id,
  });
};

export const useAddHomework = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async (payload) => {
      const response = await Post(HOMEWORK.addHomework, payload, token);
      if (response?.data?.status) return response.data;
      throw new Error(response?.data?.message || "Failed to create homework");
    },
    onSuccess: () => invalidateHomework(queryClient),
  });
};

export const useUpdateHomework = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async ({ id, ...payload }) => {
      const response = await Post(HOMEWORK.updateHomework + id, payload, token);
      if (response?.data?.status !== false) return response.data;
      throw new Error(response?.data?.message || "Failed to update homework");
    },
    onSuccess: (_data, variables) => invalidateHomework(queryClient, variables?.id),
  });
};

export const useDeleteHomework = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async (id) => {
      const response = await Get(HOMEWORK.deleteHomework + id, token);
      if (response?.status !== false) return response;
      throw new Error(response?.message || "Failed to delete homework");
    },
    onSuccess: () => invalidateHomework(queryClient),
  });
};
